import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { JobParameterService } from './common/job-parameter.service';
import { AuthenticationService } from './system/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class ApplyService extends JobParameterService {
  private datePipe: DatePipe = new DatePipe('zh-cn');

  constructor(
    protected httpClient: HttpClient,
    protected store: Store,
  ) {
    super(httpClient, store);
  }

  async addJob(job: any) {
    return await this.httpClient.post('plan/api/business/apply/add', job).toPromise();
  }

  async getApplyHistory(
    pageNum: number, pageSize: number, callerId: string, beginDate?: Date, endDate?: Date,
    typeId?: any, priority?: string, status?: string, repairerId?: string): Promise<any> {
    let condition = '?pageNum=' + pageNum + '&pageSize=' + pageSize;
    if (callerId != null) {
      condition += '&callerId=' + callerId;
    }
    if (beginDate != null) {
      condition += '&startTime=' + this.datePipe.transform(beginDate, 'yyyy-MM-dd 00:00:00');
    }
    if (endDate != null) {
      condition += '&endTime=' + this.datePipe.transform(endDate, 'yyyy-MM-dd 23:59:59');
    }
    if (typeId != null) {
      condition += '&businessTypeId=' + typeId.toString();
    }
    if (priority != null) {
      condition += '&priority=' + priority;
    }
    if (status != null && status !== '') {
      condition += '&status=' + status;
    }
    if (repairerId != null && repairerId !== '') {
      condition += '&repairerId=' + repairerId;
    }
    const result: any = await this.httpClient.get('plan/api/business/apply/getApplyHistory' + condition).toPromise();
    if (result == null || result.code !== 200 || result.data == null || result.data.list == null) {
      // 请求数据失败
      return null;
    }
    if (this.jobLevels == null) {
      await this.initParam();
    }
    const list = result.data.list;
    const data = [];
    for (const item of list) {
      item.levelName = this.jobLevels ? this.jobLevels.get(item.jlNo) : null;
      item.typeName = this.jobTypes ? this.jobTypes.get(item.jtNo) : null;
      item.statusName = this.jobStatus ? this.jobStatus.get(item.status) : null;
      data.push(item);
    }
    return {
      total: result.data.total,
      data
    };
  }

  async getJob(callerId: string, jobId: string) {
    const params = new HttpParams()
      .set('callerId', callerId)
      .set('jobId', jobId);
    const result: any = await this.httpClient.get('plan/api/business/apply/queryJob', { params }).toPromise();
    if (result == null || result.code !== 200 || result.data == null) {
      return null;
    }
    if (this.jobLevels == null) {
      await this.initParam();
    }
    const item = result.data;
    item.levelName = this.jobLevels ? this.jobLevels.get(item.jlNo) : null;
    item.typeName = this.jobTypes ? this.jobTypes.get(item.jtNo) : null;
    item.statusName = this.jobStatus ? this.jobStatus.get(item.status) : null;
    if (item.isEquipment === '1') {
      item.deviceName = '未实现固定资产名称获取';
    }
    // 为确保评星显示正常，将string转为number
    if (typeof item.starEvaluationCaller === 'string') {
      item.starEvaluationCaller = Number.parseFloat(item.starEvaluationCaller);
    }
    // 为确保评星显示正常，将string转为number
    if (typeof item.starEvaluationAdmin === 'string') {
      item.starEvaluationAdmin = Number.parseFloat(item.starEvaluationAdmin);
    }
    // 附件
    const attachments = await this.getJobAttachment(callerId, item.jno);
    if (attachments != null && attachments.length > 0) {
      item.attachments = [];
      for (const attachment of attachments) {
        item.attachments.push({
          url: 'attachmentServer' + attachment.url,
        });
      }
    } else {
      item.attachments = null;
    }
    // 维修前附件
    const beforeAttachments = await this.getJobBeforeAttachment(callerId, item.jno);
    if (beforeAttachments != null && beforeAttachments.length > 0) {
      item.beforeRecordAttachments = [];
      for (const attachment of beforeAttachments) {
        item.beforeRecordAttachments.push({
          url: 'attachmentServer' + attachment.url,
        });
      }
    } else {
      item.beforeRecordAttachments = null;
    }
    // 维修后附件
    const completedAttachments = await this.getJobCompletedAttachment(callerId, item.jno);
    if (completedAttachments != null && completedAttachments.length > 0) {
      item.completedRecordAttachments = [];
      for (const attachment of completedAttachments) {
        item.completedRecordAttachments.push({
          url: 'attachmentServer' + attachment.url,
        });
      }
    } else {
      item.completedRecordAttachments = null;
    }
    return result.data;
  }

  async getJobTraces(callerId: string, jobId: string): Promise<any[]> {
    const params = new HttpParams()
      .set('callerId', callerId)
      .set('jobId', jobId);
    const result: any = await this.httpClient.get('plan/api/business/apply/queryJobTraces', { params }).toPromise();
    if (result == null || result.code !== 200 || result.data == null || result.data.length <= 0) {
      return null;
    }
    return result.data;
  }

  async getJobAttachment(callerId: string, jobId: string) {
    const params = new HttpParams()
      .set('callerId', callerId)
      .set('jobId', jobId);
    const result: any = await this.httpClient.get('plan/api/business/apply/queryJobAttachment',
      { params }).toPromise();
    if (result == null || result.code !== 200 || result.data == null) {
      return null;
    }
    return result.data;
  }

  async getJobBeforeAttachment(callerId: string, jobId: string) {
    const params = new HttpParams()
      .set('callerId', callerId)
      .set('jobId', jobId);
    const result: any = await this.httpClient.get('plan/api/business/apply/queryJobBeforeAttachment',
      { params }).toPromise();
    if (result == null || result.code !== 200 || result.data == null) {
      return null;
    }
    return result.data;
  }

  async getJobCompletedAttachment(callerId: string, jobId: string) {
    const params = new HttpParams()
      .set('callerId', callerId)
      .set('jobId', jobId);
    const result: any = await this.httpClient.get('plan/api/business/apply/queryJobCompletedAttachment',
      { params }).toPromise();
    if (result == null || result.code !== 200 || result.data == null) {
      return null;
    }
    return result.data;
  }

  async addJobNote(note: any) {
    return await this.httpClient.post('plan/api/business/apply/addNote', note).toPromise();
  }

  async appraise(data: any) {
    return await this.httpClient.post('plan/api/business/apply/appraise', data).toPromise();
  }

  fillRepairJobId(jobId: string, groupId: string, sheetContent: string) {
    const data = { jobId, groupId, sheetContent };
    return this.httpClient.post('plan/api/business/apply/addJobRepairId', data);
  }
}
